import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
    LandingPageTemplateListItemDto,
    LandingPageTemplateSaveRequestDto,
    LandingPageTemplateStatusDto,
    LandingPageVersionHtmlDto,
} from '@/types/landingPage'

/**
 * Workspace landing-page templates (ORB-1103). A template is addressed by its source web: the web whose landing page
 * was saved as one. Refusals come back as 422 with the reason as the message, which the global handler shows.
 */
export const landingPageTemplatesApi = {
    /** Most recently saved first. `forWebId` keeps only the templates a page on that web can start from now. */
    listTemplates: (
        workspaceId: string,
        forWebId?: string,
    ): Promise<ApiResponse<LandingPageTemplateListItemDto[]>> =>
        apiClient.get(
            `/api/Workspaces/${workspaceId}/LandingPageTemplates${forWebId ? `?forWebId=${encodeURIComponent(forWebId)}` : ''}`,
        ),

    /** Whether the web's landing page is a template, whether it may be saved as one, and the version it would copy. */
    getTemplateStatus: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageTemplateStatusDto>> =>
        apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Template`),

    /** Saves the web's landing page as a template, or renames it when it already is one. */
    saveTemplate: (
        workspaceId: string,
        webId: string,
        request: LandingPageTemplateSaveRequestDto,
    ): Promise<ApiResponse<LandingPageTemplateStatusDto>> =>
        apiClient.put(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Template`, request),

    /** Stops the web's landing page being a template; the page itself is not changed. 204 on success. */
    removeTemplate: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<void>> =>
        apiClient.delete(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Template`),

    /** Both pages of the version a template start would copy now, tokens resolved against the template's own web. */
    getTemplatePreview: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageVersionHtmlDto>> =>
        apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Template/Preview`),
}
